import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { MdCheckCircle, MdErrorOutline, MdLockOutline, MdStorefront } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { authorizeWooCommerceConnection, getWooCommerceConnection } from '../../../services/Http/WooCommerceOnboardingApi';

const WooCommerceConnect = ({ platform = 'woocommerce' }) => {
    const platformName = platform === 'shopware' ? 'Shopware' : 'WooCommerce';
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session');
    const [data, setData] = useState(null);
    const [selectedOrganization, setSelectedOrganization] = useState('');
    const [selectedVenue, setSelectedVenue] = useState('');
    const [venueName, setVenueName] = useState('');
    const [consent, setConsent] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const connectionRequest = useRef(0);

    useEffect(() => {
        let active = true;
        connectionRequest.current += 1;
        setData(null);
        setSelectedOrganization('');
        setSelectedVenue('');
        setVenueName('');
        setConsent(false);
        setError('');
        setLoading(true);
        setSubmitting(false);
        if (!sessionId) {
            setError(t('PluginConnect.incomplete'));
            setLoading(false);
            return;
        }

        getWooCommerceConnection(sessionId, platform)
            .then((result) => {
                if (!active) return;
                setData(result);
                const first = result.organizations?.[0];
                if (first) setSelectedOrganization(first.id);
                if (first?.venues?.length) setSelectedVenue(first.venues[0].id);
                setVenueName(`${result.session?.store_name || platformName} Online Store`);
            })
            .catch((requestError) => {
                if (!active) return;
                setError(requestError?.response?.data?.message || t('PluginConnect.expired'));
            })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; connectionRequest.current += 1; };
    }, [platform, platformName, sessionId, t]);

    const selected = useMemo(
        () => data?.organizations?.find((organization) => organization.id === selectedOrganization),
        [data, selectedOrganization]
    );
    const venues = selected?.venues || [];

    const authorize = async () => {
        if (!selectedOrganization || !consent || submitting) return;
        const request = connectionRequest.current;
        setSubmitting(true);
        setError('');
        try {
            const result = await authorizeWooCommerceConnection(sessionId, {
                organization_id: selectedOrganization,
                venue_id: selectedVenue || null,
                venue_name: selectedVenue ? null : venueName,
                consent: true,
            }, platform);
            if (request !== connectionRequest.current) return;
            window.location.assign(result.redirect_url);
        } catch (requestError) {
            if (request !== connectionRequest.current) return;
            setError(requestError?.response?.data?.message || t('PluginConnect.failed'));
            setSubmitting(false);
        }
    };

    if (loading) {
        return <Page><Panel><StatusText>{t('PluginConnect.loading')}</StatusText></Panel></Page>;
    }

    if (error && !data) {
        return <Page><Panel><ErrorIcon /><Title>{t('PluginConnect.unavailable')}</Title><Message>{error}</Message></Panel></Page>;
    }

    const organizations = data?.organizations || [];
    const session = data?.session || {};

    return (
        <Page>
            <Panel>
                <Brand>DigiWarranty</Brand>
                <Header>
                    <StoreIcon />
                    <div>
                        <Title>{t('PluginConnect.title', { platform: platformName })}</Title>
                        <Message>{t('PluginConnect.intro')}</Message>
                    </div>
                </Header>

                <StoreSummary>
                    <SummaryItem><span>{t('PluginConnect.store')}</span><strong>{session.store_name || session.site_url}</strong></SummaryItem>
                    <SummaryItem><span>{t('PluginConnect.website')}</span><strong>{session.site_url}</strong></SummaryItem>
                    <SummaryItem><span>{t('PluginConnect.catalog')}</span><strong>{t('PluginConnect.catalogValue', { products: session.product_count || 0, discounts: session.discount_count || 0 })}</strong></SummaryItem>
                </StoreSummary>

                {organizations.length === 0 ? (
                    <Notice>
                        <strong>{t('PluginConnect.noOrganization')}</strong>
                        <span>{t('PluginConnect.noOrganizationHelp')}</span>
                    </Notice>
                ) : (
                    <Form>
                        <Field>
                            <label htmlFor="organization">{t('PluginConnect.organization')}</label>
                            <select id="organization" value={selectedOrganization} disabled={submitting} onChange={(event) => {
                                setSelectedOrganization(event.target.value);
                                const next = data.organizations.find((organization) => organization.id === event.target.value);
                                setSelectedVenue(next?.venues?.[0]?.id || '');
                                setConsent(false);
                            }}>
                                {organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
                            </select>
                        </Field>
                        {venues.length > 0 && <Field>
                            <label htmlFor="venue-select">{t('PluginConnect.venue')}</label>
                            <select id="venue-select" value={selectedVenue} disabled={submitting} onChange={(event) => setSelectedVenue(event.target.value)}>
                                {venues.map((venue) => <option key={venue.id} value={venue.id}>{venue.name}</option>)}
                                <option value="">{t('PluginConnect.createVenue')}</option>
                            </select>
                        </Field>}
                        {!selectedVenue && <Field>
                            <label htmlFor="venue-name">{t('PluginConnect.venueName')}</label>
                            <input id="venue-name" value={venueName} disabled={submitting} maxLength={255} onChange={(event) => setVenueName(event.target.value)} />
                        </Field>}
                        <PermissionList>
                            <strong>{t('PluginConnect.permissions')}</strong>
                            <span><MdCheckCircle /> {t('PluginConnect.permissionCatalog')}</span>
                            <span><MdCheckCircle /> {t('PluginConnect.permissionReceipts')}</span>
                            <span><MdCheckCircle /> {t('PluginConnect.permissionVenue', { platform: platformName })}</span>
                        </PermissionList>
                        <Consent>
                            <input id="connection-consent" type="checkbox" checked={consent} disabled={submitting} onChange={(event) => setConsent(event.target.checked)} />
                            <label htmlFor="connection-consent">{t('PluginConnect.consent', { platform: platformName, organization: selected?.name || t('PluginConnect.organization') })}</label>
                        </Consent>
                        {error && <InlineError><MdErrorOutline />{error}</InlineError>}
                        <Actions>
                            <Security><MdLockOutline /> {t('PluginConnect.security')}</Security>
                            <PrimaryButton type="button" disabled={!consent || !selectedOrganization || submitting} onClick={authorize}>
                                {submitting ? t('PluginConnect.connecting') : t('PluginConnect.connect')}
                            </PrimaryButton>
                        </Actions>
                    </Form>
                )}
            </Panel>
        </Page>
    );
};

export default WooCommerceConnect;

const Page = styled.main`
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 32px 20px;
    background: #f3f4f6;
    color: #111827;
`;

const Panel = styled.section`
    width: min(720px, 100%);
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 28px;
    box-shadow: 0 12px 32px rgba(17, 24, 39, 0.08);
`;

const Brand = styled.div`
    color: #821692;
    font-size: 15px;
    font-weight: 800;
    margin-bottom: 20px;
`;

const Header = styled.header`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const StoreIcon = styled(MdStorefront)`
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    color: #821692;
`;

const ErrorIcon = styled(MdErrorOutline)`
    width: 36px;
    height: 36px;
    color: #b42318;
    margin-bottom: 12px;
`;

const Title = styled.h1`
    margin: 0;
    font-size: 24px;
    line-height: 1.25;
    letter-spacing: 0;
`;

const Message = styled.p`
    margin: 6px 0 0;
    color: #6b7280;
    line-height: 1.55;
`;

const StoreSummary = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1px;
    margin: 24px 0;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #e5e7eb;

    @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const SummaryItem = styled.div`
    min-width: 0;
    padding: 14px;
    background: #f9fafb;
    span, strong { display: block; overflow-wrap: anywhere; }
    span { color: #6b7280; font-size: 12px; margin-bottom: 4px; }
    strong { font-size: 14px; }
`;

const Form = styled.div`
    display: grid;
    gap: 18px;
`;

const Field = styled.div`
    display: grid;
    gap: 7px;
    label { font-size: 13px; font-weight: 700; }
    input, select {
        width: 100%;
        min-height: 42px;
        padding: 8px 11px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        background: #fff;
        box-sizing: border-box;
    }
    input:focus, select:focus { outline: 2px solid rgba(130, 22, 146, 0.18); border-color: #821692; }
`;

const PermissionList = styled.div`
    display: grid;
    gap: 9px;
    padding: 16px;
    border-left: 3px solid #821692;
    background: #f9fafb;
    span { display: flex; align-items: center; gap: 8px; color: #374151; }
    svg { color: #18864b; flex: 0 0 auto; }
`;

const Consent = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    input { margin-top: 3px; accent-color: #821692; }
    label { line-height: 1.45; }
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-top: 4px;
    @media (max-width: 560px) { align-items: stretch; flex-direction: column; }
`;

const Security = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    font-size: 12px;
`;

const PrimaryButton = styled.button`
    min-height: 42px;
    padding: 9px 18px;
    border: 0;
    border-radius: 6px;
    background: #821692;
    color: #fff;
    font-weight: 700;
    cursor: pointer;
    &:disabled { background: #c4a3ca; cursor: not-allowed; }
`;

const Notice = styled.div`
    display: grid;
    gap: 5px;
    padding: 16px;
    border: 1px solid #f2c94c;
    border-radius: 6px;
    background: #fffbeb;
    span { color: #6b7280; }
`;

const InlineError = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    color: #b42318;
    font-size: 13px;
`;

const StatusText = styled.p`
    margin: 0;
    color: #6b7280;
`;
